import { ValidationError } from "@core/domain/errors/validation.error";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { MaterialSubtype } from "@entity/domain/models/material/material-subtype.model";
import { inject, injectable } from "inversify";
import {
    MaterialType,
    MaterialTypeEnum,
} from "../../domain/models/material/material-type.model";
import {
    CreateMaterial,
    Material,
    MaterialSearchFilters,
    Materials,
} from "../../domain/models/material/material.model";
import { MetricUnit } from "../../domain/models/material/metric-unit.model";
import { MaterialDatasource } from "../datasources/material.datasource";
import { MaterialTypeEnumMapper } from "../mappers/material/material-type-enum.mapper";
import { EntityMaterialError } from "@entity/domain/errors/entity.error";
import { MaterialMapperError } from "@entity/domain/errors/material/material-mapper.error";

@injectable()
export class MaterialRepository {
    constructor(
        @inject(MaterialDatasource)
        private readonly materialDatasource: MaterialDatasource,
        @inject(MaterialTypeEnumMapper)
        private readonly materialTypeEnumMapper: MaterialTypeEnumMapper,
    ) { }

    async getAllMaterialTypes(): Promise<
        Either<EntityMaterialError, MaterialType[]>
    > {
        return this.materialDatasource.fetchAllTypes();
    }

    async getAllMaterialSubtypesByTypeId(
        typeId: MaterialTypeEnum,
    ): Promise<Either<EntityMaterialError, MaterialSubtype[]>> {
        const typeIdDto = this.materialTypeEnumMapper.mapToDto(typeId);
        return this.materialDatasource.fetchAllSubtypesByTypeId(typeIdDto);
    }

    async getAllMetricUnits(): Promise<Either<EntityMaterialError, MetricUnit[]>> {
        return this.materialDatasource.fetchAllMetricUnits();
    }

    async searchAllBy(
        pagination: Pagination,
        filters?: MaterialSearchFilters,
    ): Promise<Either<EntityMaterialError, Materials>> {
        const materialsResult = await this.materialDatasource.fetchAllBy(
            pagination,
            filters,
        );

        if (materialsResult.isLeft())
            return Either.Left(materialsResult.getLeftOrThrow());

        return Either.Right(materialsResult.getOrThrow());
    }

    async getMaterial(
        materialId: number,
    ): Promise<Either<EntityMaterialError | MaterialMapperError, Material>> {
        const materialsResult =
            await this.materialDatasource.fetchById(materialId);

        if (materialsResult.isLeft())
            return Either.Left(materialsResult.getLeftOrThrow());

        return Either.Right(materialsResult.getOrThrow());
    }

    async create(
        material: CreateMaterial,
    ): Promise<Either<ValidationError | EntityMaterialError | MaterialMapperError, Material>> {
        return this.materialDatasource.create(material);
    }

    async edit(
        material: Material,
    ): Promise<Either<ValidationError | EntityMaterialError | MaterialMapperError, Material>> {
        return this.materialDatasource.edit(material);
    }

    async delete(materialId: number): Promise<Either<EntityMaterialError, boolean>> {
        return this.materialDatasource.delete(materialId);
    }
}
